import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Security,
  DevicesOther,
  Refresh,
  Delete,
  DeleteSweep,
  AccessTime,
  LocationOn,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { en } from '../translations/en';
import { TrustedDevice, DeviceTrustSettings } from '../types/device-trust';
import { logger } from '../utils/logger';

export const DeviceTrust: React.FC = () => {
  const theme = useTheme();
  const {
    getTrustedDevices,
    revokeTrustedDevice,
    revokeAllTrustedDevices,
    getDeviceTrustSettings,
    getDeviceStatus,
  } = useAuth();

  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [settings, setSettings] = useState<DeviceTrustSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<string | null>(null);

  // Dialog state for revoke confirmation
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeAllDialogOpen, setRevokeAllDialogOpen] = useState(false);
  const [deviceToRevoke, setDeviceToRevoke] = useState<TrustedDevice | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [deviceList, deviceSettings, status] = await Promise.all([
        getTrustedDevices(),
        getDeviceTrustSettings(),
        getDeviceStatus(),
      ]);

      setDevices(deviceList);
      setSettings(deviceSettings);
      setDeviceStatus(status);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load device trust information';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [getTrustedDevices, getDeviceTrustSettings, getDeviceStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRevokeDevice = async () => {
    if (!deviceToRevoke) return;

    setActionLoading(true);
    setError(null);

    try {
      await revokeTrustedDevice(deviceToRevoke.id);
      setSuccess(`Device "${deviceToRevoke.deviceName}" trust revoked`);
      setRevokeDialogOpen(false);
      setDeviceToRevoke(null);
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to revoke device trust';
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAll = async () => {
    setActionLoading(true);
    setError(null);

    try {
      const count = await revokeAllTrustedDevices();
      setSuccess(`All ${count} device(s) trust revoked`);
      setRevokeAllDialogOpen(false);
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to revoke all device trust';
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (isoString: string): string => {
    try {
      return new Date(isoString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      logger.debug('Failed to format date', error);
      return isoString;
    }
  };

  const getExpirationStatus = (expiresAt: string): { color: 'success' | 'warning' | 'error'; label: string } => {
    // Handle missing or empty dates
    if (!expiresAt) {
      return { color: 'warning', label: 'Unknown' };
    }

    try {
      const expiration = new Date(expiresAt);

      // Check for Invalid Date (getTime() returns NaN for invalid dates)
      if (isNaN(expiration.getTime())) {
        return { color: 'warning', label: 'Unknown' };
      }

      const now = new Date();
      const daysRemaining = Math.ceil((expiration.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysRemaining <= 0) {
        return { color: 'error', label: 'Expired' };
      } else if (daysRemaining <= 2) {
        return { color: 'warning', label: `Expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}` };
      } else {
        return { color: 'success', label: `Expires in ${daysRemaining} days` };
      }
    } catch (error) {
      logger.debug('Failed to parse device expiry date', error);
      return { color: 'warning', label: 'Unknown' };
    }
  };

  const isDeviceRegistered = deviceStatus !== 'not_registered' && deviceStatus !== null;

  return (
    <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <DevicesOther sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h6">{en.deviceTrust.title}</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {settings && (
              <Chip
                label={`${settings.deviceCount}/${settings.maxDevices} devices`}
                size="small"
                color={settings.deviceCount >= settings.maxDevices ? 'error' : 'default'}
              />
            )}
            <Button
              size="small"
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              {en.deviceTrust.refresh}
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {en.deviceTrust.description}
        </Typography>

        {settings && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Device trust expires after {settings.trustDurationDays} days.
            MFA re-verification required every {settings.mfaReverificationDays} days.
          </Typography>
        )}

        {!isDeviceRegistered && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {en.deviceTrust.notEnabled}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : devices.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No trusted devices. After completing MFA, you can choose to trust a device to skip MFA on future logins.
          </Alert>
        ) : (
          <>
            <List sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
              {devices.map((device, index) => {
                const expStatus = getExpirationStatus(device.expiresAt);
                return (
                  <React.Fragment key={device.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{device.deviceName}</Typography>
                            <Chip
                              label={expStatus.label}
                              size="small"
                              color={expStatus.color}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" component="div">
                              <AccessTime sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                              Trusted: {formatDate(device.trustedAt)}
                            </Typography>
                            {device.lastUsedAt && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                Last used: {formatDate(device.lastUsedAt)}
                              </Typography>
                            )}
                            {device.trustedIpRange && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                <LocationOn sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                IP Range: {device.trustedIpRange}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Revoke trust">
                          <IconButton
                            edge="end"
                            onClick={() => {
                              setDeviceToRevoke(device);
                              setRevokeDialogOpen(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>

            {devices.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweep />}
                  onClick={() => setRevokeAllDialogOpen(true)}
                  fullWidth
                >
                  Revoke All Devices
                </Button>
              </Box>
            )}
          </>
        )}

        <Box sx={{
          mt: 2,
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Security sx={{ mr: 1, fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography variant="caption" color="text.secondary">
              {en.deviceTrust.securityNote}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {en.deviceTrust.securityDescription}
          </Typography>
        </Box>

        {/* Revoke Single Device Dialog */}
        <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
          <DialogTitle>Revoke Device Trust?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to revoke trust for &quot;{deviceToRevoke?.deviceName}&quot;?
              This device will require MFA verification on the next sign-in.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevokeDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleRevokeDevice}
              color="error"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
            >
              Revoke
            </Button>
          </DialogActions>
        </Dialog>

        {/* Revoke All Devices Dialog */}
        <Dialog open={revokeAllDialogOpen} onClose={() => setRevokeAllDialogOpen(false)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Revoke All Device Trust?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will revoke trust for all {devices.length} device(s).
              All devices will require MFA verification on the next sign-in.
              <br /><br />
              <strong>This action cannot be undone.</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevokeAllDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleRevokeAll}
              color="error"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
            >
              Revoke All
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
